/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

/* tslint:disable: no-duplicate-imports */
import { generateProductImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import MsDyn365, {
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { AsyncResult, ProductRefinerValue, ProductSearchCriteria, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import { searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import {
    BaseCollectionInput,
    createBaseCollectionInput
} from './base-collection-action';

import { getVirtualRefinerNames, getVirtualRefinerValues } from '../../../Utilities/plp-url-utils';
import { ListPageStateInput } from '../list-page-state';
import getCurrentCategory, { CurrentCategoryInput } from './get-current-category';
// VSI Customization 7294 Start
import getProductRefinerHierarchy, { RefinersByCollectionInput } from './get-refiners-for-collection';
// VSI Customization 7294 End
import { IFullProductsSearchResultsWithCount } from './IFullProductsSearchResultsWithCount';

/**
 * GetFullProductsByCollection Action Input
 */
export class GetFullProductsByCollectionInput extends BaseCollectionInput implements IActionInput {
    public getCacheObjectType = () => 'FullProductSearchResult';
    public dataCacheType = () => {
        if (
            this.pageType !== 'Category' ||
            (this.refiners && this.refiners.length > 0) ||
            (this.queryResultSettings && this.queryResultSettings.Sorting && this.queryResultSettings.Sorting.Columns && this.queryResultSettings.Sorting.Columns.length > 0)
        ) {
            return 'request';
        } else {
            return 'application';
        }
    };
}

/**
 * createInput function which creates and actionInput used to fetch products for a list page.
 */
const createInput = (args: ICreateActionContext<{ itemsPerPage: number; includedAttributes: boolean | undefined; }>): IActionInput => {
    const input = createBaseCollectionInput(args, GetFullProductsByCollectionInput);

    // Set Top
    if (input.queryResultSettings.Paging && args.config) {
        input.queryResultSettings.Paging.Top = args.config.itemsPerPage || 1;
    }

    // Set Skip
    if (input.queryResultSettings.Paging && args.requestContext?.query?.skip) {
        input.queryResultSettings.Paging.Skip = +args.requestContext.query.skip;
    }

    input.queryResultSettings.count = true;

    return input;
};

/**
 * Action function to fetch products for a list page
 */
// tslint:disable-next-line: max-func-body-length cyclomatic-complexity
async function action(
    input: GetFullProductsByCollectionInput,
    ctx: IActionContext
): Promise<IFullProductsSearchResultsWithCount> {
    let promise: AsyncResult<ProductSearchResult[]>;
    let searchProductId;
    const refinerBoundString = 'LeftValueBoundString';
    const searchCriteriaInput: ProductSearchCriteria = {};
    searchCriteriaInput.Context = { ChannelId: ctx.requestContext.apiSettings.channelId, CatalogId: ctx.requestContext.apiSettings.catalogId };
    searchCriteriaInput.Refinement = [...new Map(input.refiners.map(item =>
        [item[refinerBoundString], item])).values()];
    searchCriteriaInput.IncludeAttributes = input.includeAttributes;
    searchCriteriaInput.SkipVariantExpansion = true;
    // VSI Customization 7924 Start
    let activeVirtualRefiners: ProductRefinerValue[] = [];
    // VSI Customization 7924 End

    if (input.pageType === 'Category' || (ctx.requestContext?.query?.categoryId)) {
        if (input.category) {
            const isCustomizeRefiner = ctx.requestContext.app.config.urlModifications && ctx.requestContext.app.config.urlModifications.length > 0;
            // Get current category for isSizeRefinement value from extension
            const categoryInput = new CurrentCategoryInput(ctx.requestContext);
            const categoryData = await getCurrentCategory(categoryInput, ctx);
            // Get all refiners data
            const refinerInput = new RefinersByCollectionInput(
                input.pageType,
                ctx.requestContext.apiSettings,
                input.queryResultSettings,
                input.refiners,
                input.category,
                input.searchText,
                input.includeAttributes
            );
            const refinerResult = await getProductRefinerHierarchy(refinerInput, ctx);

            const isSizeRefinement = categoryData.ExtensionProperties?.find((attr) => attr.Key === 'SizeRefinementEnable')
                ?.Value?.BooleanValue;
            const configVirtualRefinerNames = getVirtualRefinerNames(ctx.requestContext, isSizeRefinement);
            // filter out refiners to only have ones which are added on config
            const filteredRefiners = refinerResult.filter((refiner) => {
                return refiner.KeyName && configVirtualRefinerNames.indexOf(refiner.KeyName.toLowerCase()) !== -1;
            });
            activeVirtualRefiners = await getVirtualRefinerValues(ctx.requestContext, filteredRefiners);

            // checking if isSizeRefinement is true and size refiner is selected so enable customization
            if (isSizeRefinement) {
                let sizeRefinerArr: (string | undefined)[] = [];
                refinerResult &&
                    refinerResult.map(refiner => {
                        if (refiner && refiner?.KeyName?.toLowerCase() === 'size') {
                            sizeRefinerArr = refiner?.Values?.map(obj => obj?.LeftValueBoundString?.toLowerCase());
                        }
                    });
                const isAnySizeSelectedArr = activeVirtualRefiners?.filter(obj =>
                    sizeRefinerArr.includes(obj?.LeftValueBoundString?.toLowerCase())
                );
                if (isAnySizeSelectedArr && isAnySizeSelectedArr.length > 0) {
                    input.isSizeRefinerAndCategory = true;
                }
            }
            if (isCustomizeRefiner) {
                // VSI Customization 7294 Start

                // get the active virtual refiner values
                if (activeVirtualRefiners.length > 0) {
                    if (input.refiners.length > 0) {
                        const concatRefiners = [...new Map(searchCriteriaInput.Refinement.concat(activeVirtualRefiners).map(item =>
                            [item[refinerBoundString], item])).values()];
                        concatRefiners[0].ExtensionProperties = [
                            {
                                Key: 'ISREQUESTOVERRIDE',
                                Value: {
                                    BooleanValue: (!input.isSizeRefinerAndCategory || input.isSizeRefinerAndCategory === undefined ? false : true),
                                    ByteValue: undefined,
                                    DateTimeOffsetValue: undefined,
                                    DecimalValue: undefined,
                                    IntegerValue: undefined,
                                    LongValue: undefined,
                                    StringValue: undefined
                                }
                            }
                        ];
                        searchCriteriaInput.Refinement = concatRefiners;
                    } else if (input.refiners.length === 0) {
                        activeVirtualRefiners[0].ExtensionProperties = [
                            {
                                Key: 'ISREQUESTOVERRIDE',
                                Value: {
                                    BooleanValue: (!input.isSizeRefinerAndCategory || input.isSizeRefinerAndCategory === undefined ? false : true),
                                    ByteValue: undefined,
                                    DateTimeOffsetValue: undefined,
                                    DecimalValue: undefined,
                                    IntegerValue: undefined,
                                    LongValue: undefined,
                                    StringValue: undefined
                                }
                            }
                        ];
                        searchCriteriaInput.Refinement = [...new Map(activeVirtualRefiners.map(item =>
                            [item[refinerBoundString], item])).values()];
                    }
                }
            }
            // VSI Customization 7294 End
            searchCriteriaInput.CategoryIds = [input.category || 0];
            promise = searchByCriteriaAsync(
                {
                    callerContext: ctx,
                    queryResultSettings: input.queryResultSettings
                },
                searchCriteriaInput
            );
        } else {
            throw new Error(
                '[GetFullProductsForCollection]Category Page Detected, but no global categoryId found'
            );
        }
    } else {
        if (input.searchText && (ctx.requestContext.query && ctx.requestContext.query.q)) {
            searchCriteriaInput.SearchCondition = input.searchText;
            promise = searchByCriteriaAsync(
                {
                    callerContext: ctx,
                    queryResultSettings: input.queryResultSettings
                },
                searchCriteriaInput
            );
        } else {
            if (input.searchText && (ctx.requestContext.query && ctx.requestContext.query.productId && ctx.requestContext.query.recommendation)) {
                const searchObject = JSON.parse(input.searchText);
                searchProductId = Number(searchObject.ProductId);
                if (Number.isNaN(searchProductId)) {
                    throw new Error('Failed to cast search product id into a number.');
                } else if (!searchObject.Recommendation) {
                    throw new Error('Failed to retrieve the Recommendation.');
                } else {
                    searchCriteriaInput.RecommendationListId = searchObject.Recommendation;
                    searchCriteriaInput.Ids = [searchProductId || 0];
                    promise = searchByCriteriaAsync(
                        {
                            callerContext: ctx,
                            queryResultSettings: input.queryResultSettings
                        },
                        searchCriteriaInput
                    );
                }
            } else {
                throw new Error(
                    '[GetFullProductsForCollection]Search Page Detected, but no q= or productId= query parameter found'
                );
            }
        }
    }

    const productSearchResults: ProductSearchResult[] = await promise;

    const mappedProducts = productSearchResults.map(productSearchResult => {
        const newImageUrl = generateProductImageUrl(
            productSearchResult,
            ctx.requestContext.apiSettings
        );

        if (newImageUrl) {
            productSearchResult.PrimaryImageUrl = newImageUrl;
        }

        return (productSearchResult);
    });

    // Update ListPageState For SSR
    if (!MsDyn365.isBrowser) {
        ctx.update(new ListPageStateInput(), { totalProductCount: promise.metadata.count || 0, activeFilters: input.refiners });

    }

    return {
        products: mappedProducts,
        count: promise.metadata.count || 0,
        // VSI Customization 7294 Start
        virtualRefiners: searchCriteriaInput.Refinement,
        minusVirtualRefiner: input.refiners
        // VSI Customization 7294 End
    };
}

export default createObservableDataAction({
    id: '@msdyn365-commerce-modules/search-result-container/get-full-products-by-collection',
    action: <IAction<IFullProductsSearchResultsWithCount>>action,
    input: createInput
});
